interface SecurityEvent {
  type: string;
  timestamp: string;
  data?: Record<string, any>;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  userId?: string;
  sessionId?: string;
  ipAddress?: string;
  userAgent?: string;
}

interface SecurityAuditConfig {
  maxLogSize: number;
  retentionDays: number;
  enableConsoleLogging: boolean;
  enableRemoteLogging: boolean;
  remoteEndpoint?: string;
}

class SecurityAuditLogger {
  private readonly STORAGE_KEY = 'security_audit_log';
  private readonly SESSION_ID_KEY = 'security_session_id';
  
  private config: SecurityAuditConfig = {
    maxLogSize: 1000, // Maximum number of log entries
    retentionDays: 30,
    enableConsoleLogging: import.meta.env.DEV,
    enableRemoteLogging: import.meta.env.PROD,
    remoteEndpoint: `${import.meta.env.VITE_API_BASE_URL || ''}/api/security/audit`
  };

  private sessionId: string;

  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.cleanupOldLogs();
  }

  /**
   * Log a security event
   */
  logSecurityEvent(type: string, data?: Record<string, any>, severity: SecurityEvent['severity'] = 'medium'): void {
    const event: SecurityEvent = {
      type,
      timestamp: new Date().toISOString(),
      data,
      severity,
      sessionId: this.sessionId,
      userAgent: navigator.userAgent,
      userId: this.getCurrentUserId()
    };

    // Add to local storage
    this.addToLocalLog(event);

    // Console logging in development
    if (this.config.enableConsoleLogging) {
      this.logToConsole(event);
    }

    // Remote logging in production
    if (this.config.enableRemoteLogging) {
      this.sendToRemoteEndpoint(event);
    }

    // Handle critical events immediately
    if (severity === 'critical') {
      this.handleCriticalEvent(event);
    }
  }

  /**
   * Log authentication events
   */
  logAuthEvent(action: 'login' | 'logout' | 'register' | 'password_change', success: boolean, data?: Record<string, any>): void {
    this.logSecurityEvent(`auth_${action}`, {
      success,
      ...data
    }, success ? 'low' : 'high');
  }

  /**
   * Log permission violations
   */
  logPermissionViolation(resource: string, action: string, data?: Record<string, any>): void {
    this.logSecurityEvent('permission_violation', {
      resource,
      action,
      ...data
    }, 'high');
  }

  /**
   * Log suspicious activity
   */
  logSuspiciousActivity(activity: string, data?: Record<string, any>): void {
    this.logSecurityEvent('suspicious_activity', {
      activity,
      ...data
    }, 'critical');
  }

  /**
   * Log data access events
   */
  logDataAccess(resource: string, action: 'read' | 'write' | 'delete', data?: Record<string, any>): void {
    this.logSecurityEvent('data_access', {
      resource,
      action,
      ...data
    }, 'low');
  }

  /**
   * Get security audit logs
   */
  getAuditLogs(filter?: {
    type?: string;
    severity?: SecurityEvent['severity'];
    startDate?: Date;
    endDate?: Date;
  }): SecurityEvent[] {
    const logs = this.getLocalLogs();
    
    if (!filter) {
      return logs;
    }

    return logs.filter(log => {
      if (filter.type && log.type !== filter.type) {
        return false;
      }
      
      if (filter.severity && log.severity !== filter.severity) {
        return false;
      }
      
      if (filter.startDate && new Date(log.timestamp) < filter.startDate) {
        return false;
      }
      
      if (filter.endDate && new Date(log.timestamp) > filter.endDate) {
        return false;
      }
      
      return true;
    });
  }

  /**
   * Export audit logs for compliance
   */
  exportAuditLogs(format: 'json' | 'csv' = 'json'): string {
    const logs = this.getLocalLogs();
    
    if (format === 'csv') {
      const headers = ['timestamp', 'type', 'severity', 'userId', 'sessionId', 'data'];
      const csvRows = [
        headers.join(','),
        ...logs.map(log => [
          log.timestamp,
          log.type,
          log.severity || '',
          log.userId || '',
          log.sessionId || '',
          JSON.stringify(log.data || {}).replace(/"/g, '""')
        ].map(field => `"${field}"`).join(','))
      ];
      return csvRows.join('\n');
    }
    
    return JSON.stringify(logs, null, 2);
  }

  /**
   * Clear audit logs (admin function)
   */
  clearAuditLogs(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.logSecurityEvent('audit_logs_cleared', {}, 'medium');
  }

  /**
   * Get security statistics
   */
  getSecurityStats(): {
    totalEvents: number;
    eventsByType: Record<string, number>;
    eventsBySeverity: Record<string, number>;
    recentEvents: SecurityEvent[];
  } {
    const logs = this.getLocalLogs();
    const now = new Date();
    const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    const eventsByType: Record<string, number> = {};
    const eventsBySeverity: Record<string, number> = {};
    
    logs.forEach(log => {
      eventsByType[log.type] = (eventsByType[log.type] || 0) + 1;
      eventsBySeverity[log.severity || 'unknown'] = (eventsBySeverity[log.severity || 'unknown'] || 0) + 1;
    });
    
    const recentEvents = logs.filter(log => new Date(log.timestamp) > last24Hours);
    
    return {
      totalEvents: logs.length,
      eventsByType,
      eventsBySeverity,
      recentEvents
    };
  }

  /**
   * Add event to local storage log
   */
  private addToLocalLog(event: SecurityEvent): void {
    try {
      const logs = this.getLocalLogs();
      logs.push(event);
      
      // Maintain max log size
      if (logs.length > this.config.maxLogSize) {
        logs.splice(0, logs.length - this.config.maxLogSize);
      }
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(logs));
    } catch (error) {
      console.error('Failed to store security audit log:', error);
    }
  }

  /**
   * Get logs from local storage
   */
  private getLocalLogs(): SecurityEvent[] {
    try {
      const logsStr = localStorage.getItem(this.STORAGE_KEY);
      return logsStr ? JSON.parse(logsStr) : [];
    } catch (error) {
      console.error('Failed to parse security audit logs:', error);
      return [];
    }
  }

  /**
   * Log to console in development
   */
  private logToConsole(event: SecurityEvent): void {
    const logMethod = event.severity === 'critical' ? 'error' : 
                     event.severity === 'high' ? 'warn' : 'log';
    
    console[logMethod](`[SECURITY AUDIT] ${event.type}:`, event);
  }

  /**
   * Send event to remote endpoint
   */
  private async sendToRemoteEndpoint(event: SecurityEvent): Promise<void> {
    // Skip remote logging if endpoint not configured or in development
    if (!this.config.remoteEndpoint || import.meta.env.DEV) {
      console.log('📝 Security audit (local only):', event.type);
      return;
    }

    // Skip if endpoint is the default placeholder (not configured)
    if (this.config.remoteEndpoint.includes('/api/security/audit')) {
      console.log('📝 Security audit (local only - endpoint not configured):', event.type);
      return;
    }

    try {
      await fetch(this.config.remoteEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event)
      });
    } catch (error) {
      // Silently fail remote logging to avoid affecting user experience
      console.warn('Failed to send security audit log to remote endpoint:', error);
    }
  }

  /**
   * Handle critical security events
   */
  private handleCriticalEvent(event: SecurityEvent): void {
    // For critical events, we might want to:
    // 1. Immediately notify administrators
    // 2. Lock the user account
    // 3. Force logout
    // 4. Show security warning
    
    console.error('CRITICAL SECURITY EVENT:', event);
    
    // Example: Force logout on critical events
    if (event.type === 'suspicious_activity' || event.type === 'multiple_failed_logins') {
      // This would typically integrate with your auth store
      localStorage.removeItem('jwt_token');
      window.location.href = '/login?reason=security';
    }
  }

  /**
   * Get or create session ID
   */
  private getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem(this.SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = this.generateSessionId();
      sessionStorage.setItem(this.SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current user ID from auth state
   */
  private getCurrentUserId(): string | undefined {
    try {
      // This would typically get the user ID from your auth store
      // For now, we'll try to extract it from the JWT token
      const token = localStorage.getItem('jwt_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId || payload.sub;
      }
    } catch (error) {
      // Ignore errors in getting user ID
    }
    return undefined;
  }

  /**
   * Clean up old logs based on retention policy
   */
  private cleanupOldLogs(): void {
    try {
      const logs = this.getLocalLogs();
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - this.config.retentionDays);
      
      const filteredLogs = logs.filter(log => new Date(log.timestamp) > cutoffDate);
      
      if (filteredLogs.length !== logs.length) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredLogs));
      }
    } catch (error) {
      console.error('Failed to cleanup old security audit logs:', error);
    }
  }
}

export const securityAuditLogger = new SecurityAuditLogger();